import { t } from 'i18next';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as yup from 'yup';

import { campaignsEndpoints } from '@/api/endpoints';
import { WhatsappTemplate, WhatsappTemplateComponent } from '@/shared/types/broadcast';
import { MAX_FIRST_MESSAGE_DELAY_MS } from '@/shared/types/campaigns';
import { IntegrationType } from '@/shared/types/integration';
import { LENGTH_M } from '@/util/validation/constants';
import { areAllTemplateVariablesFilled, emailPattern } from '@/util/validation/patterns';
import { createNameFieldSchema } from '@/util/validation/schema-builders';
import { newTag } from '@/util/validation/schemas/dialog-schemas';

import { DraftCampaignSubscriber } from './redux/actions';

// Server caps `nba_config.goal` at 255 characters. Enforce it client-side so the
// form never POSTs an over-length goal (which returns a raw backend error).
export const GOAL_MAX_LENGTH = 255;

const delayRangeError = () => t('campaigns.build.voice_settings.delay_range_error');

export const quickAddSchema = (existingSubscribers: DraftCampaignSubscriber[] = []) =>
  yup.object({
    name: yup
      .string()
      .trim()
      .max(LENGTH_M)
      .required(() => t('validation.required')),
    email: yup
      .string()
      .trim()
      .nullable()
      .test(
        'email-or-phone-on-email',
        () => t('contacts.errors.email_or_phone_required'),
        function (value) {
          const phone = (this.parent?.phone as string | undefined)?.trim();
          const email = value?.trim();
          return !!(email || phone);
        }
      )
      .test(
        'email-format',
        () => t('campaigns.build.invalid_email'),
        function (value) {
          const trimmedValue = value?.trim();
          if (!trimmedValue) {
            return true;
          }
          return emailPattern.test(trimmedValue);
        }
      )
      .test(
        'email-unique',
        () => t('campaigns.email_already_used'),
        function (value) {
          const trimmed = value?.trim()?.toLowerCase();
          if (!trimmed) {
            return true;
          }
          return !existingSubscribers.some((sub) => sub.email?.toLowerCase() === trimmed);
        }
      ),
    phone: yup
      .string()
      .trim()
      .nullable()
      .test(
        'email-or-phone-on-phone',
        () => t('contacts.errors.email_or_phone_required'),
        function (value) {
          const email = (this.parent?.email as string | undefined)?.trim();
          const phone = value?.trim();
          return !!(email || phone);
        }
      )
      .test(
        'phone-format',
        () => t('campaigns.build.invalid_phone'),
        function (value) {
          if (!value?.trim()) {
            return true;
          }
          return isValidPhoneNumber(value);
        }
      )
      .test(
        'phone-unique',
        () => t('campaigns.phone_already_used'),
        function (value) {
          const trimmed = value?.trim();
          if (!trimmed) {
            return true;
          }
          return !existingSubscribers.some((sub) => sub.phone === trimmed);
        }
      ),
    keepAdding: yup.boolean(),
  });

export const createCampaignSetupSchema = (currentName?: string) =>
  yup.object({
    name: createNameFieldSchema({
      getEndpoint: campaignsEndpoints.checkName,
      max: 48,
      excludeName: currentName,
    }),
    audience: yup.mixed().nullable().optional(), // We'll handle audience validation separately since it depends on Redux state
    aiAgent: yup.string().required(() => t('campaigns.setup.ai_agent_required')),
    tags: yup
      .array()
      .of(yup.object({ label: newTag, value: newTag }))
      .min(1, () => t('campaigns.setup.tags_required'))
      .required(() => t('campaigns.setup.tags_required')),
  });

// Deprecated: Use createCampaignSetupSchema() instead
export const campaignSetupSchema = createCampaignSetupSchema();

export const campaignBuildSchema = yup.object({
  primarySender: yup
    .object({
      integrationId: yup.string().required(() => t('campaigns.build.integration_id_required')),
      integrationType: yup.string().required(),
      content: yup
        .string()
        .when('integrationType', {
          is: (type: string) =>
            type === IntegrationType.INFOBIP_SMS ||
            type === IntegrationType.INFOBIP_RCS ||
            type === IntegrationType.MSTAT_VIBER ||
            type === IntegrationType.EMAIL ||
            type === IntegrationType.TWILIO_VOICE ||
            type === IntegrationType.TELNYX_VOICE ||
            type === IntegrationType.AGORA_VOICE,
          then: (schema) => schema.required(() => t('validation.required')),
          otherwise: (schema) => schema.optional(),
        }),
      subject: yup
        .string()
        .when('integrationType', {
          is: 'email',
          then: (schema) => schema.required(() => t('validation.required')),
          otherwise: (schema) => schema.optional(),
        }),
      template: yup.object({ id: yup.string().optional() }).optional(),
      headerParameters: yup
        .array()
        .of(yup.object({ name: yup.mixed().optional() }))
        .optional(),
      bodyParameters: yup
        .array()
        .of(yup.object({ name: yup.mixed().optional() }))
        .optional(),
      footerParameters: yup
        .array()
        .of(yup.object({ name: yup.mixed().optional() }))
        .optional(),
      headerMediaParam: yup
        .object({ type: yup.string().optional(), value: yup.mixed().optional() })
        .optional(),
    })
    .test(
      'whatsapp-template-required',
      () => t('campaigns.build.whatsapp_template_required'),
      function (value) {
        if (value?.integrationType === 'whatsapp') {
          const hasTemplate = !!(value?.template?.id && value.template.id.trim() !== '');
          if (!hasTemplate) {
            return this.createError({
              message: t('campaigns.build.whatsapp_template_required'),
              path: 'primarySender.template',
            });
          }
        }
        return true;
      }
    )
    .test(
      'whatsapp-primary-media-header-required',
      () => t('validation.required'),
      function (value) {
        if (value?.integrationType === 'whatsapp' && value?.template?.id) {
          const template = value.template as WhatsappTemplate;
          const headerMediaParam = value.headerMediaParam;

          // Check if template has a media header (IMAGE, VIDEO, DOCUMENT)
          const headerComponent = template?.components?.find(
            (c: WhatsappTemplateComponent) => c.type === 'HEADER'
          );
          const isMediaHeader =
            headerComponent?.format &&
            headerComponent.format.toLowerCase() !== 'text' &&
            headerComponent.format.toLowerCase() !== 'location';

          if (isMediaHeader) {
            // If template has media header, require both type and value
            if (!headerMediaParam?.type || headerMediaParam.type.trim() === '') {
              return this.createError({
                message: t('validation.required'),
                path: 'primarySender.headerMediaParam.type',
              });
            }

            if (!headerMediaParam?.value || headerMediaParam.value.toString().trim() === '') {
              return this.createError({
                message: t('validation.required'),
                path: 'primarySender.headerMediaParam.value',
              });
            }
          }
        }
        return true;
      }
    )
    .test(
      'whatsapp-primary-header-variables-required',
      () => t('campaigns.build.header_parameter_required'),
      function (value) {
        if (value?.integrationType === 'whatsapp' && value?.template?.id) {
          if (!areAllTemplateVariablesFilled(value.headerParameters)) {
            return this.createError({
              message: t('campaigns.build.header_parameter_required'),
              path: 'primarySender.headerParameters',
            });
          }
        }
        return true;
      }
    )
    .test(
      'whatsapp-primary-body-variables-required',
      () => t('campaigns.build.body_parameter_required'),
      function (value) {
        if (value?.integrationType === 'whatsapp' && value?.template?.id) {
          if (!areAllTemplateVariablesFilled(value.bodyParameters)) {
            return this.createError({
              message: t('campaigns.build.body_parameter_required'),
              path: 'primarySender.bodyParameters',
            });
          }
        }
        return true;
      }
    )
    .test(
      'whatsapp-primary-footer-variables-required',
      () => t('campaigns.build.footer_parameter_required'),
      function (value) {
        if (value?.integrationType === 'whatsapp' && value?.template?.id) {
          if (!areAllTemplateVariablesFilled(value.footerParameters)) {
            return this.createError({
              message: t('campaigns.build.footer_parameter_required'),
              path: 'primarySender.footerParameters',
            });
          }
        }
        return true;
      }
    )
    .required(),
  fallbackSenders: yup
    .array()
    .of(
      yup
        .object({
          integrationId: yup.string().optional(),
          integrationType: yup.string().optional(),
          content: yup.string().optional(),
          subject: yup.string().optional(),
          template: yup.object({ id: yup.string().optional() }).optional(),
          enabled: yup.boolean().default(false),
          waitTimeMinutes: yup.number().optional(),
          headerParameters: yup
            .array()
            .of(yup.object({ name: yup.mixed().optional() }))
            .optional(),
          bodyParameters: yup
            .array()
            .of(yup.object({ name: yup.mixed().optional() }))
            .optional(),
          footerParameters: yup
            .array()
            .of(yup.object({ name: yup.mixed().optional() }))
            .optional(),
          headerMediaParam: yup
            .object({ type: yup.string().optional(), value: yup.mixed().optional() })
            .optional(),
        })
        // Validate integrationId is required when this sender should be validated
        .test('integration-id-required', function (value) {
          const { path } = this;
          const index = parseInt(path.match(/\[(\d+)\]/)?.[1] || '0', 10);
          // Access parent form values using this.from (array of ancestors)
          // this.from[1] is the root form object
          const rootForm = this.from?.[1]?.value;
          const fallbackIfNotDelivered = rootForm?.fallbackIfNotDelivered;
          const fallbackIfNoResponse = rootForm?.fallbackIfNoResponse;

          // Index 0 = "not delivered" fallback, Index 1+ = "no response" fallbacks
          const shouldValidate =
            (index === 0 && fallbackIfNotDelivered) || (index >= 1 && fallbackIfNoResponse);

          if (shouldValidate && !value?.integrationId) {
            const message =
              index === 0
                ? t('campaigns.build.fallback_sender_required')
                : t('campaigns.build.followup_sender_required');
            return this.createError({ message, path: `${path}.integrationId` });
          }
          return true;
        })
        // Validate content for SMS/RCS/Email/Voice
        .test('content-required', function (value) {
          const { path } = this;
          const index = parseInt(path.match(/\[(\d+)\]/)?.[1] || '0', 10);
          const rootForm = this.from?.[1]?.value;
          const fallbackIfNotDelivered = rootForm?.fallbackIfNotDelivered;
          const fallbackIfNoResponse = rootForm?.fallbackIfNoResponse;

          const shouldValidate =
            (index === 0 && fallbackIfNotDelivered) || (index >= 1 && fallbackIfNoResponse);

          if (!shouldValidate || !value?.integrationId) {
            return true;
          }

          const requiresContent =
            value.integrationType === IntegrationType.INFOBIP_SMS ||
            value.integrationType === IntegrationType.INFOBIP_RCS ||
            value.integrationType === IntegrationType.MSTAT_VIBER ||
            value.integrationType === IntegrationType.EMAIL ||
            value.integrationType === IntegrationType.TWILIO_VOICE ||
            value.integrationType === IntegrationType.TELNYX_VOICE ||
            value.integrationType === IntegrationType.AGORA_VOICE;

          if (requiresContent && (!value.content || value.content.trim() === '')) {
            const message =
              index === 0
                ? t('campaigns.build.fallback_sms_content_required')
                : t('campaigns.build.followup_content_required');
            return this.createError({ message, path: `${path}.content` });
          }
          return true;
        })
        // Validate subject for Email
        .test('subject-required', function (value) {
          const { path } = this;
          const index = parseInt(path.match(/\[(\d+)\]/)?.[1] || '0', 10);
          const rootForm = this.from?.[1]?.value;
          const fallbackIfNotDelivered = rootForm?.fallbackIfNotDelivered;
          const fallbackIfNoResponse = rootForm?.fallbackIfNoResponse;

          const shouldValidate =
            (index === 0 && fallbackIfNotDelivered) || (index >= 1 && fallbackIfNoResponse);

          if (!shouldValidate || !value?.integrationId) {
            return true;
          }

          if (
            value.integrationType === IntegrationType.EMAIL &&
            (!value.subject || value.subject.trim() === '')
          ) {
            const message =
              index === 0
                ? t('campaigns.build.fallback_email_subject_required')
                : t('campaigns.build.followup_email_subject_required');
            return this.createError({ message, path: `${path}.subject` });
          }
          return true;
        })
        // Validate WhatsApp template
        .test('whatsapp-template-required', function (value) {
          const { path } = this;
          const index = parseInt(path.match(/\[(\d+)\]/)?.[1] || '0', 10);
          const rootForm = this.from?.[1]?.value;
          const fallbackIfNotDelivered = rootForm?.fallbackIfNotDelivered;
          const fallbackIfNoResponse = rootForm?.fallbackIfNoResponse;

          const shouldValidate =
            (index === 0 && fallbackIfNotDelivered) || (index >= 1 && fallbackIfNoResponse);

          if (!shouldValidate || !value?.integrationId) {
            return true;
          }

          if (
            value.integrationType === 'whatsapp' &&
            (!value.template?.id || value.template.id.trim() === '')
          ) {
            const message =
              index === 0
                ? t('campaigns.build.fallback_whatsapp_template_required')
                : t('campaigns.build.followup_whatsapp_template_required');
            return this.createError({ message, path: `${path}.template` });
          }
          return true;
        })
        // Validate WhatsApp media header
        .test('whatsapp-media-header-required', function (value) {
          const { path } = this;
          const index = parseInt(path.match(/\[(\d+)\]/)?.[1] || '0', 10);
          const rootForm = this.from?.[1]?.value;
          const fallbackIfNotDelivered = rootForm?.fallbackIfNotDelivered;
          const fallbackIfNoResponse = rootForm?.fallbackIfNoResponse;

          const shouldValidate =
            (index === 0 && fallbackIfNotDelivered) || (index >= 1 && fallbackIfNoResponse);

          if (!shouldValidate || !value?.integrationId) {
            return true;
          }
          if (value.integrationType !== 'whatsapp' || !value.template?.id) {
            return true;
          }

          const template = value.template as WhatsappTemplate;
          const headerMediaParam = value.headerMediaParam;

          const headerComponent = template?.components?.find(
            (c: WhatsappTemplateComponent) => c.type === 'HEADER'
          );
          const isMediaHeader =
            headerComponent?.format &&
            headerComponent.format.toLowerCase() !== 'text' &&
            headerComponent.format.toLowerCase() !== 'location';

          if (isMediaHeader) {
            if (!headerMediaParam?.type || headerMediaParam.type.trim() === '') {
              return this.createError({
                message: t('validation.required'),
                path: `${path}.headerMediaParam.type`,
              });
            }
            if (!headerMediaParam?.value || headerMediaParam.value.toString().trim() === '') {
              return this.createError({
                message: t('validation.required'),
                path: `${path}.headerMediaParam.value`,
              });
            }
          }
          return true;
        })
        // Validate WhatsApp header parameters
        .test('whatsapp-header-params-required', function (value) {
          const { path } = this;
          const index = parseInt(path.match(/\[(\d+)\]/)?.[1] || '0', 10);
          const rootForm = this.from?.[1]?.value;
          const fallbackIfNotDelivered = rootForm?.fallbackIfNotDelivered;
          const fallbackIfNoResponse = rootForm?.fallbackIfNoResponse;

          const shouldValidate =
            (index === 0 && fallbackIfNotDelivered) || (index >= 1 && fallbackIfNoResponse);

          if (!shouldValidate || !value?.integrationId) {
            return true;
          }
          if (value.integrationType !== 'whatsapp' || !value.template?.id) {
            return true;
          }

          if (!areAllTemplateVariablesFilled(value.headerParameters)) {
            return this.createError({
              message: t('campaigns.build.header_parameter_required'),
              path: `${path}.headerParameters`,
            });
          }
          return true;
        })
        // Validate WhatsApp body parameters
        .test('whatsapp-body-params-required', function (value) {
          const { path } = this;
          const index = parseInt(path.match(/\[(\d+)\]/)?.[1] || '0', 10);
          const rootForm = this.from?.[1]?.value;
          const fallbackIfNotDelivered = rootForm?.fallbackIfNotDelivered;
          const fallbackIfNoResponse = rootForm?.fallbackIfNoResponse;

          const shouldValidate =
            (index === 0 && fallbackIfNotDelivered) || (index >= 1 && fallbackIfNoResponse);

          if (!shouldValidate || !value?.integrationId) {
            return true;
          }
          if (value.integrationType !== 'whatsapp' || !value.template?.id) {
            return true;
          }

          if (!areAllTemplateVariablesFilled(value.bodyParameters)) {
            return this.createError({
              message: t('campaigns.build.body_parameter_required'),
              path: `${path}.bodyParameters`,
            });
          }
          return true;
        })
        // Validate WhatsApp footer parameters
        .test('whatsapp-footer-params-required', function (value) {
          const { path } = this;
          const index = parseInt(path.match(/\[(\d+)\]/)?.[1] || '0', 10);
          const rootForm = this.from?.[1]?.value;
          const fallbackIfNotDelivered = rootForm?.fallbackIfNotDelivered;
          const fallbackIfNoResponse = rootForm?.fallbackIfNoResponse;

          const shouldValidate =
            (index === 0 && fallbackIfNotDelivered) || (index >= 1 && fallbackIfNoResponse);

          if (!shouldValidate || !value?.integrationId) {
            return true;
          }
          if (value.integrationType !== 'whatsapp' || !value.template?.id) {
            return true;
          }

          if (!areAllTemplateVariablesFilled(value.footerParameters)) {
            return this.createError({
              message: t('campaigns.build.footer_parameter_required'),
              path: `${path}.footerParameters`,
            });
          }
          return true;
        })
    )
    // Array-level validation for minimum senders
    .test('fallback-array-required', function (fallbackSenders) {
      // At array level, this.parent gives us the root form object
      const fallbackIfNotDelivered = this.parent?.fallbackIfNotDelivered;
      const fallbackIfNoResponse = this.parent?.fallbackIfNoResponse;

      if (fallbackIfNotDelivered && (!fallbackSenders || fallbackSenders.length === 0)) {
        return this.createError({
          message: t('campaigns.build.fallback_sender_required'),
          path: 'fallbackSenders',
        });
      }

      if (fallbackIfNoResponse && (!fallbackSenders || fallbackSenders.length < 2)) {
        return this.createError({
          message: t('campaigns.build.followup_sender_required'),
          path: 'fallbackSenders',
        });
      }

      return true;
    })
    .optional(),
  fallbackIfNotDelivered: yup.boolean().default(false),
  fallbackIfNoResponse: yup.boolean().default(false),
  // Campaign-level voice opener settings; both keys stay optional so that an
  // untouched control keeps the platform default
  voiceSettings: yup
    .object({
      // Null is how the form spells "reset to the platform default"
      firstMessageInterruptible: yup.boolean().nullable().optional(),
      firstMessageDelayMs: yup
        .number()
        .nullable()
        .typeError(delayRangeError)
        .integer(delayRangeError)
        .min(0, delayRangeError)
        .max(MAX_FIRST_MESSAGE_DELAY_MS, delayRangeError)
        .optional(),
    })
    .optional(),
  headerParameters: yup
    .array()
    .of(
      yup.object({
        name: yup.mixed().required(() => t('campaigns.build.header_parameter_required')),
      })
    )
    .test(
      'whatsapp-header-variables-required',
      () => t('campaigns.build.header_parameter_required'),
      function (value, context) {
        const primarySender = context.parent.primarySender;
        if (primarySender?.integrationType === 'whatsapp' && primarySender?.template?.id) {
          return areAllTemplateVariablesFilled(value);
        }
        return true;
      }
    )
    .optional(),
  bodyParameters: yup
    .array()
    .of(
      yup.object({ name: yup.mixed().required(() => t('campaigns.build.body_parameter_required')) })
    )
    .test(
      'whatsapp-body-variables-required',
      () => t('campaigns.build.body_parameter_required'),
      function (value, context) {
        const primarySender = context.parent.primarySender;
        if (primarySender?.integrationType === 'whatsapp' && primarySender?.template?.id) {
          return areAllTemplateVariablesFilled(value);
        }
        return true;
      }
    )
    .optional(),
  footerParameters: yup
    .array()
    .of(
      yup.object({
        name: yup.mixed().required(() => t('campaigns.build.footer_parameter_required')),
      })
    )
    .test(
      'whatsapp-footer-variables-required',
      () => t('campaigns.build.footer_parameter_required'),
      function (value, context) {
        const primarySender = context.parent.primarySender;
        if (primarySender?.integrationType === 'whatsapp' && primarySender?.template?.id) {
          return areAllTemplateVariablesFilled(value);
        }
        return true;
      }
    )
    .optional(),
});
